import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FiShare2 } from "react-icons/fi";

export interface PdfVisualizacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfBlob: Blob | null;
  nomeArquivoSugerido: string;
  titulo: string;
  tituloIframe: string;
}

const PdfVisualizacaoModal = ({
  isOpen,
  onClose,
  pdfBlob,
  nomeArquivoSugerido,
  titulo,
  tituloIframe,
}: PdfVisualizacaoModalProps) => {
  const toast = useToast();
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const [compartilhando, setCompartilhando] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (!pdfBlob) {
      setSrcUrl(null);
      return;
    }
    const u = URL.createObjectURL(pdfBlob);
    objectUrlRef.current = u;
    setSrcUrl(u);
  }, [pdfBlob]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const salvarPeloNavegador = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeArquivoSugerido;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: "PDF solicitado",
      description:
        "O celular pode ter aberto a pasta Arquivos, Downloads ou uma barra embaixo. Se não achar, tente de novo pelo botão verde acima (Compartilhar) e escolha Salvar no dispositivo ou WhatsApp.",
      status: "info",
      duration: 10000,
      isClosable: true,
    });
  };

  const compartilharOuGuardar = async () => {
    if (!pdfBlob) {
      return;
    }
    const file = new File([pdfBlob], nomeArquivoSugerido, { type: "application/pdf" });
    setCompartilhando(true);
    try {
      const nav = navigator as Navigator & {
        share?: (data: ShareData) => Promise<void>;
        canShare?: (data: ShareData) => boolean;
      };
      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        try {
          await nav.share({ files: [file], title: titulo });
        } catch (e) {
          const err = e as Error;
          if (err.name == "AbortError") {
            return;
          }
          salvarPeloNavegador(file);
        }
      } else {
        salvarPeloNavegador(file);
      }
    } finally {
      setCompartilhando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent
        bg="white"
        _dark={{ bg: "gray.800" }}
        maxW="100vw"
        h="100vh"
        m={0}
        rounded="none"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader color="gray.800" _dark={{ color: "gray.100" }} pr={12}>
          {titulo}
        </ModalHeader>
        <ModalCloseButton />
        <Box
          px={3}
          py={3}
          borderBottomWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
          _dark={{ bg: "gray.900", borderColor: "gray.600" }}
        >
          <Button
            leftIcon={<FiShare2 />}
            colorScheme="green"
            size="lg"
            width="full"
            minH={14}
            onClick={compartilharOuGuardar}
            isLoading={compartilhando}
            isDisabled={!pdfBlob}
            whiteSpace="normal"
          >
            Compartilhar ou guardar PDF
          </Button>
        </Box>
        <ModalBody p={0} flex="1" display="flex" flexDirection="column" minH={0}>
          {srcUrl && (
            <iframe
              title={tituloIframe}
              src={srcUrl}
              width="100%"
              style={{ flex: 1, minHeight: "50vh", border: "none" }}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PdfVisualizacaoModal;
